import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../auth/AuthContext';
import { useToast } from './Toast';
import { extractErrorMessage } from '../utils/errors';

const API_URL = process.env.REACT_APP_API_URL || '/api';

type StepType = 'PowerAction' | 'ApplyTags' | 'RemoveTags' | 'Wait';

interface RunbookStep {
  type: StepType;
  resourceIds: string[];
  action?: string;
  tags: Record<string, string>;
  tagNames: string[];
  waitSeconds?: number;
}

interface Runbook {
  id?: string;
  name: string;
  description: string;
  steps: RunbookStep[];
  createdBy?: string;
  updatedAtUtc?: string;
}

interface StepResult { stepIndex: number; type: StepType; success: boolean; message: string; }
interface Execution {
  id: string; runbookId: string; runbookName: string; executedBy: string; mode: string;
  dryRun: boolean; startedAtUtc: string; finishedAtUtc?: string; success?: boolean | null;
  stepResults: StepResult[];
}

interface VmOption { id: string; name: string; powerState: string; }

const STEP_LABEL: Record<StepType, string> = {
  PowerAction: '⚡ Power action', ApplyTags: '🏷 Apply tags', RemoveTags: '✂️ Remove tags', Wait: '⏱ Wait',
};

const emptyStep = (type: StepType): RunbookStep =>
  ({ type, resourceIds: [], tags: {}, tagNames: [], action: type === 'PowerAction' ? 'Stop' : undefined, waitSeconds: type === 'Wait' ? 30 : undefined });

function describeStep(s: RunbookStep): string {
  switch (s.type) {
    case 'PowerAction': return `${s.action} × ${s.resourceIds.length} VM`;
    case 'ApplyTags': return `+${Object.keys(s.tags).length} tag(s) × ${s.resourceIds.length}`;
    case 'RemoveTags': return `−${s.tagNames.length} tag(s) × ${s.resourceIds.length}`;
    case 'Wait': return `${s.waitSeconds}s`;
  }
}

// ── Step editor (admin) ───────────────────────────────────────────────────────

const StepEditor: React.FC<{
  step: RunbookStep; vms: VmOption[];
  onChange: (s: RunbookStep) => void; onRemove: () => void;
}> = ({ step, vms, onChange, onRemove }) => {
  const [tagKey, setTagKey] = useState('');
  const [tagValue, setTagValue] = useState('');

  const toggleVm = (id: string) => onChange({
    ...step,
    resourceIds: step.resourceIds.includes(id)
      ? step.resourceIds.filter(x => x !== id)
      : [...step.resourceIds, id],
  });

  return (
    <div style={{ background: '#1a1a1e', border: '1px solid #444', borderRadius: 6, padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <select value={step.type} onChange={e => onChange(emptyStep(e.target.value as StepType))} style={{ minWidth: 150 }}>
          {(Object.keys(STEP_LABEL) as StepType[]).map(t => <option key={t} value={t}>{STEP_LABEL[t]}</option>)}
        </select>
        {step.type === 'PowerAction' && (
          <select value={step.action} onChange={e => onChange({ ...step, action: e.target.value })} style={{ minWidth: 130 }}>
            {['Start', 'Stop', 'Restart', 'Deallocate'].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        {step.type === 'Wait' && (
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', color: '#b0b0b0', fontSize: '0.85rem' }}>
            seconds:
            <input type="number" min={1} max={300} value={step.waitSeconds ?? 30}
              onChange={e => onChange({ ...step, waitSeconds: parseInt(e.target.value) || 1 })}
              style={{ width: 80 }} />
          </label>
        )}
        <div style={{ flex: 1 }} />
        <button className="btn-small btn-danger" onClick={onRemove}>✕</button>
      </div>

      {step.type !== 'Wait' && (
        <div style={{ maxHeight: 120, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {vms.map(vm => (
            <label key={vm.id} style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: '0.8rem', color: '#b0b0b0', background: '#25252a', borderRadius: 4, padding: '0.15rem 0.5rem', cursor: 'pointer', margin: 0 }}>
              <input type="checkbox" checked={step.resourceIds.includes(vm.id)} onChange={() => toggleVm(vm.id)} style={{ width: 13, height: 13 }} />
              {vm.name}
            </label>
          ))}
        </div>
      )}

      {step.type === 'ApplyTags' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
          {Object.entries(step.tags).map(([k, v]) => (
            <span key={k} style={{ background: '#007bff22', color: '#4da6ff', border: '1px solid #007bff44', borderRadius: 10, padding: '0.1rem 0.5rem', fontSize: '0.78rem' }}>
              {k}={v}
              <button onClick={() => { const t = { ...step.tags }; delete t[k]; onChange({ ...step, tags: t }); }}
                style={{ background: 'none', border: 'none', color: '#4da6ff', cursor: 'pointer', padding: '0 0 0 4px' }}>✕</button>
            </span>
          ))}
          <input placeholder="key" value={tagKey} onChange={e => setTagKey(e.target.value)} style={{ width: 110, padding: '0.25rem 0.5rem', fontSize: '0.8rem' }} />
          <input placeholder="value" value={tagValue} onChange={e => setTagValue(e.target.value)} style={{ width: 110, padding: '0.25rem 0.5rem', fontSize: '0.8rem' }} />
          <button className="btn-small btn-secondary" onClick={() => {
            if (tagKey.trim()) { onChange({ ...step, tags: { ...step.tags, [tagKey.trim()]: tagValue.trim() } }); setTagKey(''); setTagValue(''); }
          }}>+ tag</button>
        </div>
      )}

      {step.type === 'RemoveTags' && (
        <input
          placeholder="Tag names, comma-separated"
          value={step.tagNames.join(', ')}
          onChange={e => onChange({ ...step, tagNames: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })}
          style={{ fontSize: '0.85rem' }}
        />
      )}
    </div>
  );
};

// ── Main panel ────────────────────────────────────────────────────────────────

const RunbooksPanel: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const isAdmin = user?.role === 'admin';
  const canRun = isAdmin || user?.role === 'operator';

  const [runbooks, setRunbooks] = useState<Runbook[]>([]);
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [vms, setVms] = useState<VmOption[]>([]);
  const [editing, setEditing] = useState<Runbook | null>(null);
  const [dryRunFlags, setDryRunFlags] = useState<Record<string, boolean>>({});
  const [expandedExec, setExpandedExec] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [rb, ex] = await Promise.all([
        axios.get<Runbook[]>(`${API_URL}/runbooks`),
        axios.get<Execution[]>(`${API_URL}/runbooks/executions`, { params: { limit: 30 } }),
      ]);
      setRunbooks(rb.data);
      setExecutions(ex.data);
    } catch (e) {
      showToast(extractErrorMessage(e, 'Failed to load runbooks.'), 'error');
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  // Poll while any execution is still running so step results stream in.
  const hasRunning = executions.some(e => e.success === null || e.success === undefined);
  useEffect(() => {
    if (!hasRunning) return;
    const interval = setInterval(load, 4000);
    return () => clearInterval(interval);
  }, [hasRunning, load]);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API_URL}/vm`, { params: { subscriptionId: 'all' } })
      .then(res => setVms(res.data.map((v: any) => ({ id: v.id, name: v.name, powerState: v.powerState }))))
      .catch(() => { /* VM list is only needed for the editor */ });
  }, [isAdmin]);

  const run = async (rb: Runbook) => {
    const dryRun = dryRunFlags[rb.id!] ?? true;
    try {
      await axios.post(`${API_URL}/runbooks/${rb.id}/run`, { dryRun });
      showToast(`${dryRun ? '[DRY RUN] ' : ''}Runbook "${rb.name}" started.`, 'info');
      await load();
    } catch (e) {
      showToast(extractErrorMessage(e, 'Failed to start runbook.'), 'error');
    }
  };

  const save = async () => {
    if (!editing) return;
    try {
      await axios.post(`${API_URL}/runbooks`, editing);
      showToast(`Runbook "${editing.name}" saved.`, 'success');
      setEditing(null);
      await load();
    } catch (e) {
      showToast(extractErrorMessage(e, 'Failed to save runbook.'), 'error');
    }
  };

  const remove = async (id: string) => {
    try {
      await axios.delete(`${API_URL}/runbooks/${id}`);
      showToast('Runbook deleted.', 'info');
      setConfirmDelete(null);
      await load();
    } catch (e) {
      showToast(extractErrorMessage(e, 'Failed to delete runbook.'), 'error');
    }
  };

  const lastExecutionFor = (id?: string) => executions.find(e => e.runbookId === id);

  const execBadge = (e: Execution) => {
    const running = e.success === null || e.success === undefined;
    const bg = running ? '#ffc10722' : e.success ? '#28a74522' : '#dc354522';
    const fg = running ? '#ffe08a' : e.success ? '#7ee2a8' : '#ff8f9a';
    return (
      <span style={{ background: bg, color: fg, border: `1px solid ${fg}44`, borderRadius: 10, padding: '0.15rem 0.55rem', fontSize: '0.72rem', fontWeight: 600 }}>
        {running ? '⏳ Running' : e.success ? '✓ OK' : '✗ Failed'}{e.dryRun ? ' · dry' : ''}
      </span>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <p style={{ margin: 0, color: '#888', fontSize: '0.9rem', maxWidth: 640 }}>
          Pre-approved operational procedures. Admins define the steps; operators execute them
          safely — dry-run first, every run lands in the audit trail, a failed step stops the procedure.
        </p>
        {isAdmin && !editing && (
          <button className="btn-primary" onClick={() => setEditing({ name: '', description: '', steps: [emptyStep('PowerAction')] })}>
            + New Runbook
          </button>
        )}
      </div>

      {editing && (
        <div style={{ background: '#25252a', border: '1px solid #007bff66', borderRadius: 8, padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <h3 style={{ margin: 0 }}>{editing.id ? 'Edit runbook' : 'New runbook'}</h3>
          <input placeholder="Name" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} />
          <input placeholder="Description (what does this procedure do, when to run it)" value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} />
          {editing.steps.map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <span style={{ color: '#666', fontSize: '0.8rem', paddingTop: '0.9rem', minWidth: 20 }}>{i + 1}.</span>
              <div style={{ flex: 1 }}>
                <StepEditor
                  step={s} vms={vms}
                  onChange={ns => setEditing({ ...editing, steps: editing.steps.map((x, j) => j === i ? ns : x) })}
                  onRemove={() => setEditing({ ...editing, steps: editing.steps.filter((_, j) => j !== i) })}
                />
              </div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn-secondary" onClick={() => setEditing({ ...editing, steps: [...editing.steps, emptyStep('PowerAction')] })}>+ Add step</button>
            <div style={{ flex: 1 }} />
            <button className="btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn-primary" onClick={save} disabled={!editing.name.trim() || editing.steps.length === 0}>Save runbook</button>
          </div>
        </div>
      )}

      {runbooks.length === 0 ? (
        <div style={{ background: '#25252a', border: '1px solid #444', borderRadius: 8, padding: '3rem', textAlign: 'center', color: '#888' }}>
          No runbooks defined yet.{isAdmin ? ' Create the first procedure with "+ New Runbook".' : ' Ask an administrator to define procedures for your team.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '2rem' }}>
          {runbooks.map(rb => {
            const last = lastExecutionFor(rb.id);
            return (
              <div key={rb.id} style={{ background: '#25252a', border: '1px solid #444', borderRadius: 8, padding: '1rem 1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontWeight: 600, color: '#f0f0f0' }}>{rb.name}</div>
                    {rb.description && <div style={{ color: '#888', fontSize: '0.85rem', marginTop: 2 }}>{rb.description}</div>}
                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                      {rb.steps.map((s, i) => (
                        <span key={i} style={{ background: '#1a1a1e', color: '#b0b0b0', border: '1px solid #444', borderRadius: 4, padding: '0.1rem 0.5rem', fontSize: '0.75rem' }}>
                          {i + 1}. {STEP_LABEL[s.type]} · {describeStep(s)}
                        </span>
                      ))}
                    </div>
                  </div>
                  {last && <span title={new Date(last.startedAtUtc).toLocaleString()}>{execBadge(last)}</span>}
                  {canRun && (
                    <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
                      <label style={{ display: 'flex', gap: 4, alignItems: 'center', color: '#b0b0b0', fontSize: '0.8rem', margin: 0, cursor: 'pointer' }}>
                        <input type="checkbox" checked={dryRunFlags[rb.id!] ?? true}
                          onChange={e => setDryRunFlags({ ...dryRunFlags, [rb.id!]: e.target.checked })}
                          style={{ width: 14, height: 14 }} />
                        Dry run
                      </label>
                      <button className="btn-primary btn-small" onClick={() => run(rb)}>
                        {(dryRunFlags[rb.id!] ?? true) ? '👁 Preview' : '▶ Run'}
                      </button>
                    </span>
                  )}
                  {isAdmin && (
                    <span style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      <button className="btn-small btn-secondary" onClick={() => setEditing(JSON.parse(JSON.stringify(rb)))}>✏️</button>
                      {confirmDelete === rb.id ? (
                        <>
                          <button className="btn-small btn-danger" onClick={() => remove(rb.id!)}>Confirm</button>
                          <button className="btn-small btn-secondary" onClick={() => setConfirmDelete(null)}>✕</button>
                        </>
                      ) : (
                        <button className="btn-small btn-danger" onClick={() => setConfirmDelete(rb.id!)}>🗑</button>
                      )}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem' }}>Recent executions</h3>
      {executions.length === 0 ? (
        <div style={{ color: '#888', fontSize: '0.9rem' }}>No executions yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {executions.map(e => (
            <div key={e.id} style={{ background: '#25252a', border: '1px solid #444', borderRadius: 6, overflow: 'hidden' }}>
              <div onClick={() => setExpandedExec(expandedExec === e.id ? null : e.id)}
                style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', padding: '0.6rem 1rem', cursor: 'pointer', flexWrap: 'wrap' }}>
                {execBadge(e)}
                <span style={{ color: '#f0f0f0', fontSize: '0.875rem', flex: 1 }}>{e.runbookName}</span>
                <span style={{ color: '#888', fontSize: '0.8rem' }}>{e.executedBy}</span>
                <span style={{ color: '#666', fontSize: '0.8rem' }}>{new Date(e.startedAtUtc).toLocaleString()}</span>
                <span style={{ color: '#666' }}>{expandedExec === e.id ? '▲' : '▼'}</span>
              </div>
              {expandedExec === e.id && (
                <div style={{ borderTop: '1px solid #333', background: '#1a1a1e', padding: '0.75rem 1rem', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {e.stepResults.length === 0 && <span style={{ color: '#888', fontSize: '0.85rem' }}>Starting…</span>}
                  {e.stepResults.map(sr => (
                    <div key={sr.stepIndex} style={{ display: 'flex', gap: 8, fontSize: '0.85rem' }}>
                      <span style={{ color: sr.success ? '#28a745' : '#dc3545', flexShrink: 0 }}>{sr.success ? '✓' : '✗'}</span>
                      <span style={{ color: '#888', flexShrink: 0 }}>{sr.stepIndex + 1}. {STEP_LABEL[sr.type]}</span>
                      <span style={{ color: '#ccc' }}>{sr.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RunbooksPanel;
